import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Flex, message, Modal, Radio} from "antd";
import {DownloadOutlined} from "@ant-design/icons";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import GroupService from "../../../../../services/GroupService";
import {LocalName} from "../../../../../utils/LocalName";

// Общая модалка выгрузки людей: одна и та же и на /p/groups, и на /p/users, и внутри группы.
// Если передан groupId — выгружается ровно эта группа, поэтому фильтры категории и статуса
// прячем: бэкенд их в этом режиме всё равно игнорирует.
const ExportPeopleModal = ({
                               open,
                               onClose,
                               groupId = null,
                               groupTitle = null,
                               defaultRoles = ['STUDENTS', 'TEACHERS'],
                               defaultCategoryId = null,
                               defaultStatus = 'ACTIVE',
                               defaultIncludeWithoutGroups = false
                           }) => {
    const {t} = useTranslation();
    const categoriesList = useSelector(state => state.categoriesList);

    const [roles, setRoles] = useState(defaultRoles);
    const [status, setStatus] = useState(defaultStatus);
    const [categoryId, setCategoryId] = useState(defaultCategoryId);
    const [includeWithoutGroups, setIncludeWithoutGroups] = useState(defaultIncludeWithoutGroups);
    const [downloading, setDownloading] = useState(false);

    // Каждое открытие начинаем с текущих фильтров страницы, а не с того, что осталось от прошлого раза.
    // Зависим только от open: defaultRoles — новый массив на каждый рендер родителя, в зависимостях он бы зациклил эффект.
    useEffect(() => {
        if (open) {
            setRoles(defaultRoles);
            setStatus(defaultStatus);
            setCategoryId(defaultCategoryId);
            setIncludeWithoutGroups(defaultCategoryId === null && defaultIncludeWithoutGroups);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function changeCategory(value) {
        setCategoryId(value);
        // «без групп» имеет смысл только для «Все категории» — при выбранной категории бэкенд флаг игнорирует
        setIncludeWithoutGroups(false);
    }

    function closeModal() {
        if (downloading) {
            return;
        }
        onClose?.();
    }

    function statusName() {
        if (status === 'ARCHIVED') {
            return t('export.statusArchived');
        }
        if (status === 'ALL') {
            return t('export.statusAll');
        }
        return t('export.statusActive');
    }

    // Windows не сохранит файл, если в имени есть \ / : * ? " < > | — а они вполне могут попасть
    // в название категории или группы
    function safeNamePart(value) {
        return (value || '').replace(/[\\/:*?"<>|]/g, '_').trim();
    }

    function buildFileName() {
        const date = dayjs().format('YYYY-MM-DD');
        if (groupId) {
            const title = safeNamePart(groupTitle);
            return title ? `Группа_${title}_${date}.xlsx` : `Группа_${date}.xlsx`;
        }
        const category = categoryId
            ? safeNamePart(LocalName.getName(categoriesList?.categories?.find((c) => c.id === categoryId)))
            : t('export.statusAll');
        return `Выгрузка_${category || t('export.statusAll')}_${safeNamePart(statusName())}_${date}.xlsx`;
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async function download() {
        if (roles.length === 0 || downloading) {
            return;
        }
        // имя считаем до запроса: пока идёт выгрузка, фильтры уже не меняются, но так надёжнее
        const filename = buildFileName();
        setDownloading(true);
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await GroupService.downloadPeopleExcel({
            categoryId: groupId ? null : categoryId,
            status: status,
            roles: roles.join(','),
            groupId: groupId,
            includeWithoutGroups: !groupId && !categoryId && includeWithoutGroups
        })
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, filename);
                onClose?.();
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
            .finally(() => {
                setDownloading(false);
            })
    }

    return (
        <Modal
            width={560}
            open={open}
            maskClosable={!downloading}
            closable={!downloading}
            title={t('export.title')}
            onCancel={closeModal}
            footer={[
                <Button key="cancel" onClick={closeModal} disabled={downloading}>
                    <IntlMessage id={'cancel'}/>
                </Button>,
                <Button
                    key="download"
                    type="primary"
                    icon={<DownloadOutlined/>}
                    loading={downloading}
                    disabled={roles.length === 0}
                    onClick={download}
                >
                    {/* label в <span>: IntlMessage отдаёт голый текстовый узел, а отступ от иконки
                        antd вешает селектором .anticon + span — без обёртки иконка липнет к тексту */}
                    <span><IntlMessage id={'export.download'}/></span>
                </Button>
            ]}
        >
            <Flex vertical gap={16} style={{width: "100%"}}>
                {groupId && groupTitle &&
                    <span style={{color: "#666"}}>
                        <IntlMessage id={'export.forGroup'}/>: <b>{groupTitle}</b>
                    </span>
                }

                <Flex vertical gap={6}>
                    <span style={{color: "#666"}}><IntlMessage id={'export.who'}/></span>
                    <Checkbox.Group value={roles} onChange={(value) => setRoles(value)}>
                        <Checkbox value={'STUDENTS'}><IntlMessage id={'ordersPage.students'}/></Checkbox>
                        <Checkbox value={'TEACHERS'}><IntlMessage id={'ordersPage.teachers'}/></Checkbox>
                    </Checkbox.Group>
                    {roles.length === 0 &&
                        <span style={{color: "#cf1322"}}><IntlMessage id={'export.pickAtLeastOne'}/></span>
                    }
                </Flex>

                {!groupId &&
                    <Flex vertical gap={6}>
                        <span style={{color: "#666"}}><IntlMessage id={'export.groupStatus'}/></span>
                        <Radio.Group
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value={'ACTIVE'}><IntlMessage id={'export.statusActive'}/></Radio.Button>
                            <Radio.Button value={'ARCHIVED'}><IntlMessage id={'export.statusArchived'}/></Radio.Button>
                            <Radio.Button value={'ALL'}><IntlMessage id={'export.statusAll'}/></Radio.Button>
                        </Radio.Group>
                    </Flex>
                }

                {!groupId &&
                    <Flex vertical gap={6}>
                        <span style={{color: "#666"}}><IntlMessage id={'export.category'}/></span>
                        <Radio.Group
                            value={categoryId}
                            onChange={(e) => changeCategory(e.target.value)}
                        >
                            <Radio value={null}>
                                <IntlMessage id={'allCategories'}/>
                            </Radio>
                            {
                                categoriesList?.categories?.map((category) => (
                                    <Radio value={category.id} key={category.id}>
                                        {LocalName.getName(category)}
                                    </Radio>
                                ))
                            }
                        </Radio.Group>
                    </Flex>
                }

                {!groupId && categoryId === null &&
                    <Checkbox
                        checked={includeWithoutGroups}
                        onChange={(e) => setIncludeWithoutGroups(e.target.checked)}
                    >
                        <IntlMessage id={'export.includeWithoutGroups'}/>
                    </Checkbox>
                }
            </Flex>
        </Modal>
    );
};

export default ExportPeopleModal;
